import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { z } from "zod";

import { api, unwrap } from "@/api/client";
import { pathForStep, useSetSession } from "@/auth/session";
import { HelpContact } from "@/components/help-contact";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Field } from "@/components/ui/field";
import { errorMessage } from "@/i18n";

import { AuthLayout } from "./auth-layout";

const schema = z.object({
  email: z.string().trim().min(1, "signIn.emailRequired").pipe(z.email("signIn.emailInvalid")),
  password: z.string().min(1, "signIn.passwordRequired"),
});
type Values = z.infer<typeof schema>;

export function SignInPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const setSession = useSetSession();
  const form = useForm<Values>({ resolver: zodResolver(schema) });
  const signIn = useMutation({
    mutationFn: async (values: Values) =>
      unwrap(await api.POST("/api/v1/auth/login", { body: values })),
    onSuccess: (session) => {
      setSession(session);
      void navigate(pathForStep(session.nextStep), { replace: true });
    },
  });
  const errors = form.formState.errors;

  return (
    <AuthLayout title={t("signIn.title")} intro={t("signIn.intro")}>
      <form
        noValidate
        className="flex flex-col gap-5"
        onSubmit={(event) => {
          void form.handleSubmit((values) => {
            signIn.mutate(values);
          })(event);
        }}
      >
        {signIn.isError ? <Alert tone="error">{errorMessage(signIn.error)}</Alert> : null}
        <Field
          label={t("signIn.email")}
          type="email"
          autoComplete="username"
          error={errors.email?.message ? t(errors.email.message) : undefined}
          {...form.register("email")}
        />
        <Field
          label={t("signIn.password")}
          type="password"
          autoComplete="current-password"
          error={errors.password?.message ? t(errors.password.message) : undefined}
          {...form.register("password")}
        />
        <Button type="submit" disabled={signIn.isPending}>
          {signIn.isPending ? t("signIn.submitting") : t("signIn.submit")}
        </Button>
      </form>
      <HelpContact title={t("help.signIn")} className="mt-6 border-t border-border pt-4" />
    </AuthLayout>
  );
}
